import React, { useRef } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';

const EASE = [0.16, 1, 0.3, 1];

/* ─────────────────────────────────────────────────────────────
   The small parts the action flows share: a progress tracker, a
   quantity stepper, a row of time slots, the tick a finished flow
   lands on, and the line a card uses to answer its own button.

   They sit beside ui/kit.js rather than in it so the kit stays the
   set every module screen is built from; these are only for screens
   that walk the reader through something with more than one step.
   ───────────────────────────────────────────────────────────── */

/* Where a request has got to. `current` is the step in progress; every
   step before it is done. Past the last step, all of them are. */
export function Steps({ steps, current, label = 'Progress' }) {
    return (
        <ol className="act-steps" aria-label={label} style={{ '--act-steps': steps.length }}>
            {steps.map((step, i) => {
                const state = i < current ? 'done' : i === current ? 'now' : 'next';
                const text = typeof step === 'string' ? step : step.label;
                const note = typeof step === 'string' ? null : step.note;

                return (
                    <li
                        key={text}
                        className={`act-steps__item act-steps__item--${state}`}
                        aria-current={state === 'now' ? 'step' : undefined}
                    >
                        <span className="act-steps__dot" aria-hidden="true">
                            {state === 'done' ? <Tick /> : null}
                        </span>
                        <span className="act-steps__label">{text}</span>
                        {note && <span className="act-steps__note">{note}</span>}
                    </li>
                );
            })}
        </ol>
    );
}

/* A count the reader nudges up and down: servings, guests, gallons. */
export function Stepper({ value, onChange, min = 0, max = 99, label, unit }) {
    const reduced = useReducedMotion();

    return (
        <div className="act-stepper" role="group" aria-label={label}>
            <button
                type="button"
                className="act-stepper__btn"
                onClick={() => onChange(Math.max(min, value - 1))}
                disabled={value <= min}
                aria-label={`One fewer${label ? `, ${label.toLowerCase()}` : ''}`}
            >
                <span aria-hidden="true">−</span>
            </button>
            <span className="act-stepper__value" aria-live="polite">
                <AnimatePresence mode="popLayout" initial={false}>
                    <motion.span
                        key={value}
                        initial={reduced ? false : { opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={reduced ? { opacity: 0 } : { opacity: 0, y: -8 }}
                        transition={{ duration: 0.25, ease: EASE }}
                    >
                        {value}
                    </motion.span>
                </AnimatePresence>
                {unit && <span className="act-stepper__unit">{unit}</span>}
            </span>
            <button
                type="button"
                className="act-stepper__btn"
                onClick={() => onChange(Math.min(max, value + 1))}
                disabled={value >= max}
                aria-label={`One more${label ? `, ${label.toLowerCase()}` : ''}`}
            >
                <span aria-hidden="true">+</span>
            </button>
        </div>
    );
}

/* Times to choose from. A taken slot stays on screen, greyed, so the
   reader can see the day is real rather than conveniently empty. */
export function Slots({ slots, value, onChange, label = 'Time' }) {
    return (
        <div className="act-slots" role="group" aria-label={label}>
            {slots.map(slot => {
                const on = value === slot.time;
                return (
                    <button
                        type="button"
                        key={slot.time}
                        className={`act-slot${on ? ' act-slot--on' : ''}`}
                        disabled={slot.taken}
                        aria-pressed={on}
                        onClick={() => onChange(on ? '' : slot.time)}
                    >
                        <span className="act-slot__time">{slot.time}</span>
                        <span className="act-slot__state">{slot.taken ? 'Taken' : on ? 'Selected' : 'Open'}</span>
                    </button>
                );
            })}
        </div>
    );
}

/* The end of a flow: a tick drawing itself, a headline, the details. */
export function Done({ title, children }) {
    const reduced = useReducedMotion();

    return (
        <div className="act-done">
            <motion.span
                className="act-done__mark"
                aria-hidden="true"
                initial={reduced ? false : { scale: 0.6, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.55, ease: EASE }}
            >
                <svg viewBox="0 0 24 24">
                    <motion.path
                        d="M5 12.5l4.5 4.5L19 7.5"
                        initial={reduced ? false : { pathLength: 0 }}
                        animate={{ pathLength: 1 }}
                        transition={{ duration: 0.5, delay: 0.2, ease: EASE }}
                    />
                </svg>
            </motion.span>
            <h4 className="act-done__title">{title}</h4>
            {children}
        </div>
    );
}

/* A card answering its own button, where the reader's eye already is.
   `tone` is ok for a confirmation and warn for something still missing. */
export function Note({ note }) {
    const reduced = useReducedMotion();

    return (
        <div className="act-note-slot" role="status" aria-live="polite">
            <AnimatePresence initial={false}>
                {note && (
                    <motion.p
                        key={note.key || note.text}
                        className={`act-note act-note--${note.tone || 'ok'}`}
                        initial={reduced ? { opacity: 0 } : { opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.4, ease: EASE }}
                    >
                        <span className="act-note__icon" aria-hidden="true">{note.tone === 'warn' ? '!' : <Tick />}</span>
                        <span>{note.text}</span>
                    </motion.p>
                )}
            </AnimatePresence>
        </div>
    );
}

export function Tick() {
    return (
        <svg className="act-tick" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M5 12.5l4.5 4.5L19 7.5" />
        </svg>
    );
}

/* "$1,182.50", for every price these screens add up. */
export const money = n => n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

/* A date input's value, `offset` days from today. */
export function iso(offset = 0) {
    const d = new Date();
    d.setDate(d.getDate() + offset);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/* A time input's value (14:30) the way the screens print one (2:30 PM). */
export function printTime(value) {
    if (!value) return '';
    const [h, m] = value.split(':').map(Number);
    const d = new Date();
    d.setHours(h, m, 0, 0);
    return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

/* A stable pseudo-random pick, so the same slot reads taken every visit
   rather than flickering between renders. */
export function hashed(text, mod) {
    let hash = 0;
    for (let i = 0; i < text.length; i++) hash = (hash * 31 + text.charCodeAt(i)) % 9973;
    return hash % mod;
}

/* The last value that was not null. A sheet keeps showing what it
   showed while it animates closed, after its item has been cleared. */
export function useLast(value) {
    const ref = useRef(value);
    if (value !== null && value !== undefined) ref.current = value;
    return ref.current;
}
